import React from 'react';
import { CheckCircle2, Circle } from 'lucide-react';
import { SectionCard } from './SectionCard';
import { formatDateTime } from '../../utils/dateFormat';

// The chosen-reviewer card (docs/workflow-ui.md §4.3).
// Every reviewed engagement-side flow names its reviewer(s) on the request
// itself; this card lists one row per stage — who is named, and whether and
// when they signed. Rendered from the server's `review` payload plus the
// request's recorded approvals:
//   review = { reviewers: { stageOne, stageTwo, stageThree }, stages, verdict,
//              myRole, waitingForPriorStage, canReview, canRejectOnly }
//   reviewers.stageOne/.stageTwo/.stageThree = { staffId, name, approved,
//              hasAccount } | null (null = the request carries no such stage)
//   approvals = [{ id, approverName, approverEmail, approverRole, stage, createdAt }]
//
// ONE ROW PER STAGE: only the named reviewer can sign a stage, so the stage
// row already is the approval record — its time sits on the same line. The
// one exception is the administrator's rescue vote, which counts towards
// every stage (services/reviewPolicy.js); a stage it satisfied says so.
//
// STAGES IN SEQUENCE: a QC6/QC7 form names one to three reviewers as its
// answers call for (docs/modules/workflow-qc.md §0); each later stage opens
// only after the one before it has signed.
// stageOne = null is a request with no pick — only an administrator can approve
// it until the requester modifies the request and names a reviewer.
// A QM Team stage (reviewer.team) names no one person: the row lists who is in
// the QM department now, and once signed says which member approved.
// The one rule the card does NOT show — a modify or re-submit voids recorded
// approvals — lives in docs/modules/workflow-qc.md.
const STAGE_META = [
  { key: 'engagement', slot: 'stageOne', word: 'first' },
  { key: 'additional', slot: 'stageTwo', word: 'second' },
  { key: 'third', slot: 'stageThree', word: 'third' },
];

const StageRow = ({ stage, pendingText }) => {
  const { reviewer, signature, rescue } = stage;
  const signed = !!(signature || rescue);
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-slate-700">
      {signed
        ? <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
        : <Circle className="w-4 h-4 text-slate-300 shrink-0" />}
      {reviewer && <span>{reviewer.name}</span>}
      {signature && (
        <span className="text-xs text-slate-500">
          {reviewer && reviewer.team && signature.approverName ? `Approved by ${signature.approverName} ` : 'Approved '}
          {formatDateTime(signature.createdAt)}
        </span>
      )}
      {!signature && rescue && (
        <span className="text-xs text-slate-500">Approved by Administrator {formatDateTime(rescue.createdAt)}</span>
      )}
      {!signed && pendingText && <span className="text-xs text-slate-500">{pendingText}</span>}
      {!signed && reviewer && !reviewer.hasAccount && !(reviewer.team && !(reviewer.memberNames || []).length) && (
        <span className="text-xs text-amber-600">
          {reviewer.team ? 'nobody in the QM department can sign in — cannot approve' : 'no account — cannot approve'}
        </span>
      )}
      {!signed && reviewer && reviewer.team && (
        <span className="basis-full pl-6 text-xs text-slate-500">
          {(reviewer.memberNames || []).length > 0
            ? `Anyone in the QM department may approve: ${reviewer.memberNames.join(', ')}`
            : 'Nobody serves in the QM department now — only an administrator can approve until someone joins'}
        </span>
      )}
    </li>
  );
};

const ApprovalsCard = ({ review, approvals = [], status, aiApproved = false }) => {
  // AI-approved (Signed Financial Statements): no reviewer ever existed — the card states the
  // machine approval instead of warning about a missing pick.
  if (aiApproved) {
    return (
      <SectionCard title="Reviewer Approvals" count="Approved">
        <p className="text-sm text-slate-700">
          Approved automatically — the AI review passed; no reviewer was required.
        </p>
      </SectionCard>
    );
  }
  if (!review) return null;
  const { verdict, reviewers } = review || {};
  const rescue = approvals.find((a) => a.approverRole === 'super_admin') || null;
  // The stages this request carries, in order; stage 1 is always listed
  // (a missing pick there is the "nobody named" warning).
  const stages = STAGE_META
    .filter((m, i) => i === 0 || !!(reviewers && reviewers[m.slot]))
    .map((m, i) => ({
      ...m,
      reviewer: (reviewers && reviewers[m.slot]) || null,
      signature: approvals.find((a) => a.approverRole !== 'super_admin'
        && (a.stage || 'engagement') === m.key) || null,
      rescue,
      done: !!(verdict && verdict[m.slot] && verdict[m.slot].satisfied),
      index: i,
    }));
  const multi = stages.length > 1;
  stages.forEach((s) => { s.open = stages.slice(0, s.index).every((p) => p.done); });
  const current = stages.find((s) => !s.done) || null;

  const count = status === 'approved' ? 'Approved'
    : multi
      ? (current ? `Stage ${current.index + 1} of ${stages.length} — ${current.word} reviewer` : `${stages.length} of ${stages.length} stages`)
      : `${verdict?.counted ?? approvals.length} of ${verdict?.required ?? 1} required`;
  // Only a request under review is waiting on anyone; a returned one waits on
  // its requester, which the status card already says.
  const pendingTextOf = (s) => {
    if (status !== 'requested') return null;
    return s.open ? 'Awaiting approval' : 'Waiting for the previous stage';
  };

  return (
    <SectionCard title="Reviewer Approvals" count={count}>
      <div className="space-y-3">
        {stages.map((s) => (
          <div key={s.key}>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1.5">
              {multi ? `Stage ${s.index + 1} — ${s.word} reviewer` : 'Reviewer'}
            </p>
            {!s.reviewer && !s.rescue ? (
              <p className="text-sm text-amber-700">
                No reviewer is named on this request — the requester should modify it and
                pick one. Until then only an administrator can approve.
              </p>
            ) : (
              <ul><StageRow stage={s} pendingText={pendingTextOf(s)} /></ul>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
};

export default ApprovalsCard;
